import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { Search, Users, UserPlus, Loader2 } from "lucide-react";
import { useEffect, useState, useCallback } from "react";
import { getFriendList, unfriend } from "../../api/friend.api";
import UserCard from "../../components/UserCard";
import UserProfileCard from "../../components/UserProfileCard";
import { Friend } from "../../types/friend.type";

const FriendListScreen = () => {
  const [friends, setFriends] = useState<Friend[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [, setLoadingUserId] = useState<number | null>(null);
  const [selectedUser, setSelectedUser] = useState<Friend | null>(null);

  const loadFriends = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await getFriendList();
      setFriends(response.data || []);
    } catch (error) {
      console.error("Error loading friends:", error);
      setFriends([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleUnfriend = useCallback(
    async (userId: number) => {
      try {
        setLoadingUserId(userId);
        await unfriend(userId);
        setFriends((prev) => prev.filter((f) => f.id !== userId));

        // Clear selected user if they were unfriended
        if (selectedUser?.id === userId) {
          setSelectedUser(null);
        }
      } catch (error) {
        console.error("Error unfriending user:", error);
      } finally {
        setLoadingUserId(null);
      }
    },
    [selectedUser?.id]
  );

  const handleMessage = useCallback((userId: number) => {
    console.log("Navigate to message with user:", userId);
    // TODO: Implement navigation to messages
  }, []);

  const handleCall = useCallback((userId: number) => {
    console.log("Start call with user:", userId);
    // TODO: Implement call functionality
  }, []);

  const handleVideoCall = useCallback((userId: number) => {
    console.log("Start video call with user:", userId);
    // TODO: Implement video call functionality
  }, []);

  const handleUserSelect = useCallback((user: Friend) => {
    setSelectedUser(user);
  }, []);

  useEffect(() => {
    loadFriends();
  }, [loadFriends]);

  return (
    <div className="flex h-screen pt-[70px] gap-4">
      {/* Left Sidebar - Friends List */}
      <div className="w-80 bg-white flex flex-col border-r border-l border-gray-50">
        {/* Header */}
        <div className="p-4 ">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-bold text-gray-900">Danh bạ</h1>
            <Button
              size="sm"
              onClick={() => (window.location.href = "/friends/suggestions")}
            >
              <UserPlus className="w-4 h-4" />
            </Button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              placeholder="Tìm kiếm..."
              className="pl-10 h-9 border-gray-200 focus:border-blue-500 focus:ring-blue-500"
            />
          </div>
        </div>

        {/* Friends List */}
        <div className="flex-1 overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              <span className="text-sm text-gray-500">Đang tải...</span>
            </div>
          ) : friends.length === 0 ? (
            <div className="p-4 text-center">
              <Users className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-sm text-gray-500">Không tìm thấy bạn bè nào</p>
            </div>
          ) : (
            <div className="p-2 px-4 space-y-1">
              {friends.map((friend) => (
                <UserCard
                  key={friend.id}
                  user={friend}
                  onClick={handleUserSelect}
                  isSelected={selectedUser?.id === friend.id}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Right Content - User Profile */}
      <div className="flex-1 flex flex-col">
        {selectedUser ? (
          <div className="p-6 overflow-y-auto">
            <UserProfileCard
              user={selectedUser}
              onMessage={handleMessage}
              onCall={handleCall}
              onVideoCall={handleVideoCall}
              onUnfriend={handleUnfriend}
            />
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Chọn một bạn bè
              </h3>
              <p className="text-gray-500 max-w-sm">
                Chọn một bạn bè từ danh sách bên trái để xem thông tin chi tiết
                và bắt đầu trò chuyện.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FriendListScreen;
